import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { micrositeAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Globe, Check, Copy, RefreshCw, Shield, ExternalLink, 
  AlertCircle, CheckCircle2, Info, Link2, Settings, Eye, HelpCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// DNS Setup Instructions for Common Registrars
const DNS_REGISTRARS = {
  namecheap: {
    name: "Namecheap",
    steps: [
      "Log in to your Namecheap account",
      "Go to Domain List → click 'Manage' next to your domain",
      "Click on 'Advanced DNS' tab",
      "Click 'Add New Record'",
      "For CNAME: Type='CNAME Record', Host='www', Value='microsites.effyrealty.com'",
      "For TXT: Type='TXT Record', Host='_effy-verify', Value='[TOKEN]'",
      "Click the checkmark to save"
    ]
  },
  godaddy: {
    name: "GoDaddy",
    steps: [
      "Log in to your GoDaddy account",
      "Go to 'My Products' → click 'DNS' next to your domain",
      "Click 'Add' in the Records section",
      "For CNAME: Type='CNAME', Name='www', Value='microsites.effyrealty.com'",
      "For TXT: Type='TXT', Name='_effy-verify', Value='[TOKEN]'",
      "Click 'Save'"
    ]
  },
  cloudflare: {
    name: "Cloudflare",
    steps: [
      "Log in to Cloudflare dashboard",
      "Select your domain",
      "Go to 'DNS' section",
      "Click 'Add record'",
      "For CNAME: Type='CNAME', Name='www', Target='microsites.effyrealty.com', Proxy=OFF",
      "For TXT: Type='TXT', Name='_effy-verify', Content='[TOKEN]'",
      "Click 'Save'"
    ]
  },
  route53: {
    name: "AWS Route 53",
    steps: [
      "Log in to AWS Console → Route 53",
      "Select your hosted zone",
      "Click 'Create record'",
      "For CNAME: Type='CNAME', Name='www', Value='microsites.effyrealty.com'",
      "For TXT: Type='TXT', Name='_effy-verify', Value='\"[TOKEN]\"'",
      "Click 'Create records'"
    ]
  },
  hostinger: {
    name: "Hostinger",
    steps: [
      "Log in to Hostinger hPanel",
      "Go to Domains → select your domain",
      "Click 'DNS / Nameservers'",
      "Under 'DNS Records', click 'Add Record'",
      "For CNAME: Type='CNAME', Name='www', Points to='microsites.effyrealty.com'",
      "For TXT: Type='TXT', Name='_effy-verify', TXT Value='[TOKEN]'",
      "Click 'Add Record'"
    ]
  }
};

const PartnerDomainPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [microsites, setMicrosites] = useState([]);
  const [selectedMicrosite, setSelectedMicrosite] = useState(null);
  const [domainInfo, setDomainInfo] = useState(null);
  const [customDomain, setCustomDomain] = useState('');
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRegistrar, setSelectedRegistrar] = useState('namecheap');
  const [verificationResult, setVerificationResult] = useState(null);

  useEffect(() => {
    loadMicrosites();
  }, []);

  const loadMicrosites = async () => {
    try {
      const res = await micrositeAPI.list();
      setMicrosites(res.data);
    } catch (error) {
      toast.error('Failed to load microsites');
    } finally {
      setLoading(false);
    }
  };

  const loadDomainInfo = async (micrositeId) => {
    try {
      const res = await axios.get(`${API}/microsites/${micrositeId}/domain-info`);
      setDomainInfo(res.data);
      setCustomDomain(res.data.custom_domain?.domain || '');
    } catch (error) {
      toast.error('Failed to load domain info');
    }
  };

  const handleSelectMicrosite = async (microsite) => {
    setSelectedMicrosite(microsite);
    await loadDomainInfo(microsite.id);
    setDialogOpen(true);
  };

  const handleSaveDomain = async () => {
    if (!selectedMicrosite) return;
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/microsites/${selectedMicrosite.id}/custom-domain`,
        null,
        { params: { custom_domain: customDomain || null } }
      );
      toast.success('Custom domain configured! Now add the DNS records below.');
      await loadDomainInfo(selectedMicrosite.id);
      loadMicrosites();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save domain');
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyDomain = async () => {
    if (!selectedMicrosite) return;
    setVerifying(true);
    setVerificationResult(null);
    try {
      const res = await axios.post(`${API}/microsites/${selectedMicrosite.id}/verify-domain`);
      setVerificationResult(res.data);
      
      if (res.data.verified) {
        toast.success('Domain verified successfully!');
        await loadDomainInfo(selectedMicrosite.id);
        loadMicrosites();
      } else {
        toast.error('DNS records not found yet. Please check the configuration.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const handleRemoveDomain = async () => {
    if (!selectedMicrosite) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/microsites/${selectedMicrosite.id}/custom-domain`,
        null,
        { params: { custom_domain: '' } }
      );
      toast.success('Custom domain removed');
      setCustomDomain('');
      setVerificationResult(null);
      await loadDomainInfo(selectedMicrosite.id);
      loadMicrosites();
    } catch (error) {
      toast.error('Failed to remove domain');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch (err) {
      // Fallback for when clipboard API is blocked (e.g., in iframes)
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success('Copied to clipboard');
      } catch (e) {
        toast.error('Copy failed - please copy manually');
      }
      document.body.removeChild(textArea);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-48" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="partner-domain-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Globe className="h-7 w-7 text-blue-600" />
          Domain Management
        </h1>
        <p className="text-muted-foreground">
          Configure custom domains for your microsites
        </p>
      </div>

      {/* Info Alert */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Add your own domain to make your microsite accessible at a professional URL like <span className="font-mono text-sm">www.yourname.com</span>
        </AlertDescription>
      </Alert>

      {/* Microsites Grid */}
      {microsites.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Globe className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium">No microsites yet</p>
            <p className="text-muted-foreground">Create a microsite first to configure domains</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {microsites.map((site) => (
            <Card key={site.id} className="hover:border-blue-300 transition-colors cursor-pointer" onClick={() => handleSelectMicrosite(site)}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold">{site.title}</h3>
                    <p className="text-sm text-muted-foreground">/{site.slug}</p>
                  </div>
                  <Badge variant={site.status === 'published' ? 'default' : 'secondary'}>
                    {site.status}
                  </Badge>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Link2 className="h-4 w-4" />
                    <span className="truncate">effyrealty.com/m/{site.slug}</span>
                  </div>
                  
                  {site.custom_domain && (
                    <div className={`flex items-center gap-2 ${site.domain_verified ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {site.domain_verified ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                      <span className="truncate">{site.custom_domain}</span>
                    </div>
                  )}
                </div>

                <Button variant="outline" size="sm" className="w-full mt-3">
                  <Settings className="h-4 w-4 mr-2" />
                  Configure Domain
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Domain Configuration Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Domain Settings - {selectedMicrosite?.title}
            </DialogTitle>
          </DialogHeader>

          {domainInfo && (
            <div className="space-y-4 mt-4">
              {/* Current URLs */}
              <div className="space-y-2">
                <Label>Available URLs</Label>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-2 bg-muted rounded text-sm">
                    <span className="font-mono truncate">{domainInfo.urls?.default}</span>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => copyToClipboard(domainInfo.urls?.default)}>
                        <Copy className="h-3 w-3" />
                      </Button>
                      <Button size="icon" variant="ghost" asChild>
                        <a href={domainInfo.urls?.default} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </Button>
                    </div>
                  </div>
                  
                  {domainInfo.urls?.builder_domain && (
                    <div className="flex items-center justify-between p-2 bg-blue-50 rounded text-sm">
                      <span className="font-mono truncate">{domainInfo.urls.builder_domain}</span>
                      <Badge variant="secondary">Builder Domain</Badge>
                    </div>
                  )}
                  
                  {domainInfo.urls?.custom_domain && (
                    <div className={`flex items-center justify-between p-2 rounded text-sm ${domainInfo.custom_domain?.verified ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                      <span className="font-mono truncate">{domainInfo.urls.custom_domain}</span>
                      <Badge variant={domainInfo.custom_domain?.verified ? 'default' : 'secondary'} className={domainInfo.custom_domain?.verified ? 'bg-emerald-600' : ''}>
                        {domainInfo.custom_domain?.verified ? 'Active' : 'Pending'}
                      </Badge>
                    </div>
                  )}
                </div>
              </div>

              {/* Custom Domain Input */}
              {domainInfo.builder_allows_partner_domains ? (
                <div className="space-y-3">
                  <Label>Custom Domain</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="www.yourdomain.com"
                      value={customDomain}
                      onChange={(e) => setCustomDomain(e.target.value)}
                    />
                    <Button onClick={handleSaveDomain} disabled={saving}>
                      {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Save'}
                    </Button>
                  </div>
                  
                  {domainInfo.custom_domain?.domain && (
                    <Button variant="outline" size="sm" onClick={handleRemoveDomain} disabled={saving}>
                      Remove Custom Domain
                    </Button>
                  )}
                </div>
              ) : (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Custom domains are disabled by the builder. Contact your builder admin to enable this feature.
                  </AlertDescription>
                </Alert>
              )}

              {/* DNS Instructions */}
              {domainInfo.custom_domain?.domain && !domainInfo.custom_domain?.verified && (
                <Alert className="bg-blue-50 border-blue-200">
                  <Info className="h-4 w-4 text-blue-600" />
                  <AlertDescription>
                    <p className="font-semibold text-blue-800 mb-2">Configure DNS Records</p>
                    
                    <div className="space-y-2 text-sm">
                      <div className="bg-white p-2 rounded border">
                        <div className="flex justify-between items-center">
                          <Badge>CNAME</Badge>
                          <Button size="sm" variant="ghost" onClick={() => copyToClipboard('microsites.effyrealty.com')}>
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                        <p className="font-mono text-xs mt-1">
                          {customDomain?.startsWith('www.') ? 'www' : '@'} → microsites.effyrealty.com
                        </p>
                      </div>
                      
                      <div className="bg-white p-2 rounded border">
                        <div className="flex justify-between items-center">
                          <Badge variant="secondary">TXT</Badge>
                          <Button size="sm" variant="ghost" onClick={() => copyToClipboard(domainInfo.custom_domain?.verification_token || '')}>
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                        <p className="font-mono text-xs mt-1 break-all">
                          _effy-verify → {domainInfo.custom_domain?.verification_token}
                        </p>
                      </div>
                    </div>

                    {/* Registrar-specific Instructions */}
                    <div className="mt-3 p-2 bg-white rounded border">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="flex items-center gap-1 text-xs">
                          <HelpCircle className="h-3 w-3" />
                          Step-by-step for your registrar
                        </Label>
                        <Select value={selectedRegistrar} onValueChange={setSelectedRegistrar}>
                          <SelectTrigger className="w-32 h-7 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(DNS_REGISTRARS).map(([key, reg]) => (
                              <SelectItem key={key} value={key}>{reg.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <ol className="list-decimal list-inside space-y-0.5 text-xs text-gray-700">
                        {DNS_REGISTRARS[selectedRegistrar]?.steps.map((step, i) => (
                          <li key={i} className={step.includes('[TOKEN]') ? 'font-medium text-blue-700' : ''}>
                            {step.replace('[TOKEN]', domainInfo.custom_domain?.verification_token || '[TOKEN]')}
                          </li>
                        ))}
                      </ol>
                    </div>

                    {/* Verification Result */}
                    {verificationResult && !verificationResult.verified && (
                      <div className="mt-3 p-2 bg-amber-50 rounded border border-amber-200 text-xs">
                        <p className="font-semibold text-amber-800 mb-1">Verification Status:</p>
                        <p className="flex items-center gap-1">
                          {verificationResult.verification_details?.txt_record?.verified ? 
                            <CheckCircle2 className="h-3 w-3 text-green-600" /> : 
                            <AlertCircle className="h-3 w-3 text-red-500" />
                          }
                          TXT: {verificationResult.verification_details?.txt_record?.message}
                        </p>
                        <p className="text-amber-700 mt-1">DNS changes can take 5 min to 48 hours to propagate.</p>
                      </div>
                    )}

                    <Button onClick={handleVerifyDomain} disabled={verifying} className="mt-3 w-full">
                      {verifying ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                      Verify Domain
                    </Button>
                  </AlertDescription>
                </Alert>
              )}

              {/* Verified Success */}
              {domainInfo.custom_domain?.verified && (
                <Alert className="bg-emerald-50 border-emerald-200">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <AlertDescription className="text-emerald-800">
                    <p className="font-semibold">Domain Active</p>
                    <p className="text-sm">
                      Your microsite is live at <span className="font-mono">{domainInfo.custom_domain.domain}</span>
                    </p>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartnerDomainPage;
